'use client';

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Snowflake } from 'lucide-react';

import { useT } from '@/hooks';
import { usePurchaseFreeze } from '@/services/query/streak.queries';
import { colors } from '@/styles/theme';
import type { StreakStatus } from '@/types/streak.types';

const FREEZE_COST_POINTS = 200;

interface StreakFreezeCardProps {
  data: StreakStatus;
}

/**
 * StreakFreezeCard
 * Explains the freeze mechanic in plain terms and lets the user spend points
 * on one. Kept separate from StreakHowItWorks since it's an action, not just
 * an explanation.
 */
export const StreakFreezeCard: React.FC<StreakFreezeCardProps> = ({ data }) => {
  const { t } = useT();
  const { mutate: purchaseFreeze, isPending } = usePurchaseFreeze();

  const canAfford = data.pointsBalance >= FREEZE_COST_POINTS;
  const pointsShort = Math.max(0, FREEZE_COST_POINTS - data.pointsBalance);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 4,
        bgcolor: 'background.paper',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 3,
            bgcolor: 'rgba(91, 140, 255, 0.12)',
            color: colors.info.main,
          }}
        >
          <Snowflake size={22} />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('streaks.freezeTitle', {
              defaultValue: `${data.freezesAvailable} freeze${data.freezesAvailable === 1 ? '' : 's'} available`,
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 480 }}>
            {t('streaks.freezeBody', {
              defaultValue:
                'A freeze automatically covers the next day you miss your target, so your streak survives instead of resetting.',
            })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          disabled={isPending || !canAfford}
          onClick={() => purchaseFreeze()}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {t('streaks.freezeBuyAction', {
            defaultValue: `Buy a freeze — ${FREEZE_COST_POINTS} pts`,
          })}
        </Button>
        {!canAfford && (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {t('streaks.freezeShort', {
              defaultValue: `Earn ${pointsShort} more points to afford one`,
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
